// File: OrderItemsList.js

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSmartBack } from '../../shared/navigation';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';

import {
  getInvoiceByDealId,
  downloadInvoiceForPaymentStages,
  cancelInvoice,
  getCheque,
  addCheque,
  getClosedDealKycStatus,
  claimExtraCharges,
  getAllPaymentHistoryForDeals,
  getDealReconcile,
  applyDealCreditBalance,
  retryInvoicePriorities,
  getStageUnlockState,
  getAllocationReport,
  importTallyLedger,
  verifyLegacyCredit
} from '../services/api/implementation/impl';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { XCircle } from 'lucide-react';
import InfoTip from '../../shared/components/ui/InfoTip';
import { DEAL_HELP, RECONCILE_HELP } from '../utils/invoiceHelp';
import Loader from '../../shared/components/ui/Loader';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import TabComponents from '../../shared/components/TabComponent';
import cancelDealIcon from '../../shared/assets/svg/cancelDeal.svg';
import SalesPayment from './SalesPayment';
import AddChequeForm from '../components/forms/AddChequeForm';
import ChequeTable from '../components/forms/ChequeTable';
import PaymentForm from '../components/forms/PaymentForm';
import ClaimExtraChargesForm from '../components/forms/ClaimExtraChargesForm';
import DebitCreditNoteForm from '../components/forms/DebitCreditNoteForm';
import AccountsKycPanel from '../components/postSales/AccountsKycPanel';
import {
  evaluateKycVerification,
  kycVerifyBadgeClasses
} from '../components/postSales/postSalesKyc';
import {
  isEmptyPdfPayload,
  axiosErrorMessage,
  triggerPdfDownload
} from '../utils/downloadPdf';
import {
  computeDealDueBreakdown,
  computeCashReceivedFromHistory,
  computeDealReconcileSnapshot,
  defaultPriceCorrectionRemarks,
  formatInrAmount,
  invoiceLateFeeColumnAmount,
  invoiceOutstandingContribution,
  isActiveInvoice,
  isCreditNoteInvoice,
  isLateFeeInvoice,
  ADJUSTMENT_REASON_COMMERCIAL_WRITE_OFF,
  ADJUSTMENT_REASON_PRICE_CORRECTION,
  buildPriceCorrectionInvoiceNumber,
  getPriceCorrectionTargetInvoiceId,
  isPriceCorrectionCreditNote,
  pickPriceCorrectionTargetInvoice,
  priceCorrectionAppliedToInvoice,
  INVOICE_SOURCE_CREDIT_NOTE
} from '../utils/invoiceDealAmounts';

const InvoiceDetails = () => {
  const userId = Cookies.get('userId');
  const { collapsed } = useSidebar();
  const { id } = useParams();
  const { onBack } = useSmartBack({ fallback: '/builder/accounts/invoice' });
  const [sortDirection, setSortDirection] = useState('none');
  const [activeTab, setActiveTab] = useState('Invoice');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelInvoiceModal, setShowCancelInvoiceModal] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);
  const [showWaveOffModal, setShowWaveOffModal] = useState(false);
  const [invoiceToWaveOff, setInvoiceToWaveOff] = useState(null);
  const [showLateFeeWriteOffModal, setShowLateFeeWriteOffModal] = useState(false);
  const [showRemainingWriteOffModal, setShowRemainingWriteOffModal] = useState(false);
  const [adjustmentReason, setAdjustmentReason] = useState(
    ADJUSTMENT_REASON_PRICE_CORRECTION
  );
  const [adjustmentRemarks, setAdjustmentRemarks] = useState('');
  const [showKycPanel, setShowKycPanel] = useState(false);

  // Payment modal state
  const [isPaid, setIsPaid] = useState(false);
  const [StatusId, setStatusId] = useState();
  const [dueAmount, setDueAmount] = useState(0);
  const [commonPay, setCommonPay] = useState(false);

  // Cheque modal state
  const [showChequeModal, setShowChequeModal] = useState(false);
  const [chequeData, setChequeData] = useState([]);

  // Claim Extra Charges modal state
  const [showClaimExtraChargesModal, setShowClaimExtraChargesModal] = useState(false);
  const [showDebitCreditNoteModal, setShowDebitCreditNoteModal] = useState(false);

  // Fetch order activities by order ID
  const {
    data: invoiceData,
    refetch: refetchInvoiceData,
    isLoading
  } = useQuery({
    queryKey: ['orderActivities', id, sortDirection],
    queryFn: () => getInvoiceByDealId(id, 1, 500, sortDirection),
    enabled: !!id,
    refetchOnWindowFocus: true
  });

  const dealIdForKyc =
    invoiceData?.data?.[0]?.closedDeal?.id ||
    invoiceData?.data?.[0]?.deal_id ||
    (Number.isFinite(Number(id)) ? Number(id) : null);

  const { data: kycStatus } = useQuery({
    queryKey: ['closedDealKycStatus', dealIdForKyc, 'invoice'],
    queryFn: () => getClosedDealKycStatus(dealIdForKyc),
    enabled: Boolean(dealIdForKyc)
  });
  const kycVerification = evaluateKycVerification(
    kycStatus || {
      kyc_verify_status: invoiceData?.data?.[0]?.closedDeal?.kyc_verify_status
    }
  );

  const { data: paymentHistoryRes, refetch: refetchPaymentHistory } = useQuery({
    queryKey: ['dealPaymentHistory', dealIdForKyc],
    queryFn: () => getAllPaymentHistoryForDeals(dealIdForKyc, 1, 20000),
    enabled: Boolean(dealIdForKyc),
    refetchOnWindowFocus: true
  });

  const { data: serverReconcile, refetch: refetchReconcile } = useQuery({
    queryKey: ['dealReconcile', dealIdForKyc],
    queryFn: () => getDealReconcile(dealIdForKyc),
    enabled: Boolean(dealIdForKyc),
    retry: 1,
    refetchOnWindowFocus: true
  });

  const { data: stageUnlockRes, refetch: refetchStageUnlock } = useQuery({
    queryKey: ['stageUnlock', dealIdForKyc],
    queryFn: () => getStageUnlockState(dealIdForKyc),
    enabled: Boolean(dealIdForKyc),
    retry: 1,
    refetchOnWindowFocus: true
  });
  const stageUnlockRows = stageUnlockRes?.data?.stages || stageUnlockRes?.stages || [];
  const [allocationReport, setAllocationReport] = useState(null);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [tallyBalanceInput, setTallyBalanceInput] = useState('');
  const [tallyAsOf, setTallyAsOf] = useState('');
  const [stageUnlockOpen, setStageUnlockOpen] = useState(false);
  const [reconcilePanelOpen, setReconcilePanelOpen] = useState(false);

  // Track page view
  useEffect(() => {
    mixpanel.track('Order Activity List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { mutate: handleInvoicePaymentStagesDownload } = useMutation({
    mutationFn: async ({ id, sourceName }) => {
      return await downloadInvoiceForPaymentStages(id, sourceName);
    },
    onSuccess: (data, variables) => {
      const { id, sourceName } = variables;
      if (isEmptyPdfPayload(data)) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }
      triggerPdfDownload(data, `Payment_Stage_${id}_${sourceName}.pdf`);
      toast.success('Invoice Downloaded');
    },
    onError: (err) => {
      toast.error(axiosErrorMessage(err, 'Error in downloading invoice'));
    }
  });

  const sanitizeToNumber = (val) => {
    if (val === null || val === undefined) return NaN;
    const s = String(val)
      .replace(/[^0-9.-]/g, '')
      .trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

  // Function to fetch cheques
  const fetchCheques = async () => {
    try {
      const dealId = invoiceData?.data?.[0]?.closedDeal?.id;
      if (dealId) {
        const response = await getCheque(dealId);
        console.log('cheque data', response);
        setChequeData(response || []);
      }
    } catch (error) {
      console.error('Error fetching cheques:', error);
      setChequeData([]);
    }
  };

  const { mutate: handleCreateCheques, isPending: isCreatingCheques } = useMutation({
    mutationFn: (payload) => {
      return addCheque(payload);
    },
    onSuccess: (data) => {
      const warnings = data?.warnings;
      if (Array.isArray(warnings) && warnings.length) {
        toast.success('Cheques saved. Some payment notes: ' + warnings[0]);
      } else if (data?.payments?.length) {
        toast.success('Cheque saved and applied as payment');
      } else {
        toast.success('Cheques Created Successfully');
      }
      setShowChequeModal(false);
      fetchCheques();
      refetchInvoiceData();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error creating cheques');
    }
  });

  const handleSortByDueAmount = (direction) => {
    if (sortDirection === direction) {
      // If clicking on the same direction, toggle it off
      setSortDirection('none');
    } else {
      // Set the new direction
      setSortDirection(direction);
    }
  };

  // Reset payments on modal close
  const resetPaidModal = () => {
    setIsPaid(false);
    setCommonPay(false);
  };

  // Handle claim extra charges success
  const handleClaimExtraChargesSuccess = (payload) => {
    console.log('Claim Extra Charges submitted:', payload);
    refetchInvoiceData();
    setShowClaimExtraChargesModal(false);
  };

  const handleDebitCreditNoteSuccess = () => {
    refetchInvoiceData();
    setShowDebitCreditNoteModal(false);
  };

  // Cheque handling functions
  const handleChequeSubmit = (payload) => {
    handleCreateCheques(payload);
  };

  const handleViewCheque = (cheque) => {
    // TODO: Implement view cheque details functionality
    console.log('View cheque:', cheque);
  };

  const handleDeleteCheque = (cheque) => {
    // TODO: Implement delete cheque functionality
    console.log('Delete cheque:', cheque);
  };

  const isStageUnlockedForInvoice = useCallback(
    (invoice) => {
      if (!invoice) return false;
      const source = String(invoice.source || '').trim();
      if (!source || source.toLowerCase() === 'latefee') return true;
      if (!stageUnlockRows.length) return true;
      const row = stageUnlockRows.find(
        (s) => String(s.stage || '').trim() === source
      );
      if (!row) return true;
      return row.state !== 'not_unlocked';
    },
    [stageUnlockRows]
  );

  const handleStatus = useCallback(
    (invoiceId, due) => {
      const inv = invoiceData?.data?.find((item) => item.id === Number(invoiceId));
      if (inv && !isStageUnlockedForInvoice(inv)) {
        toast.error('Stage not unlocked by management');
        return;
      }
      setStatusId(invoiceId);
      setDueAmount(due);
      setIsPaid(true);
      setCommonPay(false);
    },
    [invoiceData?.data, isStageUnlockedForInvoice]
  );

  const openCancelInvoiceModal = (poItem) => {
    setInvoiceToCancel(poItem.id);
    setShowCancelInvoiceModal(true);
  };

  const confirmCancelInvoice = async () => {
    if (!invoiceToCancel) {
      toast.error('Invoice id not found');
      return;
    }
    setIsSubmitting(true);
    try {
      await cancelInvoice(invoiceToCancel);
      toast.success('Cancel requested — pending management approval');
      refetchInvoiceData();
      setIsSubmitting(false);
      setShowCancelInvoiceModal(false);
      setInvoiceToCancel(null);
    } catch (result) {
      toast.error(
        result?.response?.data?.message || result?.message || 'Error requesting invoice cancel'
      );
      setIsSubmitting(false);
    }
  };

  // Request cancel (management approval) — same flow as cancel button
  const handleWaveOff = (invoiceId) => {
    setInvoiceToCancel(invoiceId);
    setShowCancelInvoiceModal(true);
  };

  const confirmWaveOff = async () => {
    await confirmCancelInvoice();
  };

  const lateFeeWriteOffCandidates = useMemo(() => {
    return (invoiceData?.data || []).filter((inv) => {
      if (!isActiveInvoice(inv) || !isLateFeeInvoice(inv)) return false;
      if (inv?.cancel_requested) return false;
      return invoiceOutstandingContribution(inv) > 0;
    });
  }, [invoiceData?.data]);

  const lateFeeWriteOffTotal = useMemo(
    () =>
      lateFeeWriteOffCandidates.reduce(
        (sum, inv) => sum + invoiceOutstandingContribution(inv),
        0
      ),
    [lateFeeWriteOffCandidates]
  );

  const confirmLateFeeWriteOff = async () => {
    if (!lateFeeWriteOffCandidates.length) {
      toast.error('No late fee invoices to write off');
      return;
    }
    setIsSubmitting(true);
    let ok = 0;
    let failed = 0;
    for (const inv of lateFeeWriteOffCandidates) {
      try {
        await cancelInvoice(inv.id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setIsSubmitting(false);
    setShowLateFeeWriteOffModal(false);
    await refetchInvoiceData();
    if (ok > 0 && failed === 0) {
      toast.success(
        `Late fee write-off requested for ${ok} invoice${ok === 1 ? '' : 's'} — pending management approval`
      );
    } else if (ok > 0) {
      toast.success(
        `Requested write-off for ${ok}; ${failed} failed — check remaining late fee rows`
      );
    } else {
      toast.error('Could not request late fee write-off');
    }
  };

  useEffect(() => {
    refetchInvoiceData();
  }, [id]);

  // Fetch cheques when invoice data is available
  useEffect(() => {
    if (invoiceData?.data?.[0]?.closedDeal?.id) {
      fetchCheques();
    }
  }, [invoiceData?.data?.[0]?.closedDeal?.id]);

  const allInvoices = invoiceData?.data || [];
  const activeInvoices = useMemo(
    () => allInvoices.filter((inv) => isActiveInvoice(inv)),
    [allInvoices]
  );
  const cancelledInvoices = useMemo(
    () =>
      allInvoices.filter(
        (inv) => inv?.cancelled || inv?.status === 'Cancelled'
      ),
    [allInvoices]
  );
  const invoicesForTab =
    activeTab === 'Cancelled' ? cancelledInvoices : activeInvoices;

  const closedDeal = invoiceData?.data?.[0]?.closedDeal;
  const dueBreakdown = useMemo(
    () =>
      computeDealDueBreakdown(
        closedDeal?.total_price || 0,
        allInvoices,
        closedDeal?.credit_balance || 0
      ),
    [closedDeal?.total_price, closedDeal?.credit_balance, allInvoices]
  );

  const paymentHistoryRows = useMemo(() => {
    const raw = paymentHistoryRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [paymentHistoryRes]);

  const cashReceived = useMemo(
    () => computeCashReceivedFromHistory(paymentHistoryRows),
    [paymentHistoryRows]
  );

  const reconcile = useMemo(() => {
    const local = computeDealReconcileSnapshot({
      baseTotalPrice: closedDeal?.total_price || 0,
      invoices: allInvoices,
      creditBalance: closedDeal?.credit_balance || 0,
      cashReceived,
      tallyLedgerBalance: serverReconcile?.tally_ledger_balance ?? null
    });
    // Prefer server status_label when present; keep local math as source for UI amounts.
    if (serverReconcile?.status_label && serverReconcile?.status) {
      return {
        ...local,
        status: serverReconcile.status || local.status,
        status_label: serverReconcile.status_label || local.status_label
      };
    }
    return local;
  }, [
    closedDeal?.total_price,
    closedDeal?.credit_balance,
    allInvoices,
    cashReceived,
    serverReconcile
  ]);

  // totalDue / remainingDue = net amount to collect (open − cash credit)
  const remainingDue = dueBreakdown.totalDue;
  const openInvoiceBalance =
    dueBreakdown.openInvoiceBalance ?? reconcile.open_invoice_balance ?? 0;
  const netAmountToCollect =
    dueBreakdown.netAmountToCollect ?? remainingDue;
  const saleRemainingBalance = dueBreakdown.saleDue;
  const lateFeeDue = dueBreakdown.lateFeeDue;
  const pricingMismatch =
    reconcile.pricing_overage > 0 && openInvoiceBalance > 0;
  const verifiedCashCredit = Number(
    reconcile.verified_cash_credit ?? reconcile.cash_credit_balance ?? 0
  );
  const creditAvailable = verifiedCashCredit > 0;

  const openAdjustmentModal = () => {
    const reason = pricingMismatch
      ? ADJUSTMENT_REASON_PRICE_CORRECTION
      : ADJUSTMENT_REASON_COMMERCIAL_WRITE_OFF;
    setAdjustmentReason(reason);
    setAdjustmentRemarks(
      reason === ADJUSTMENT_REASON_PRICE_CORRECTION
        ? defaultPriceCorrectionRemarks(
            reconcile.closed_sale,
            reconcile.schedule_total
          )
        : ''
    );
    setShowRemainingWriteOffModal(true);
  };

  const refreshDealMoney = async () => {
    await Promise.all([
      refetchInvoiceData(),
      refetchPaymentHistory(),
      refetchReconcile()
    ]);
  };

  const confirmRemainingBalanceWriteOff = async () => {
    const dealId =
      closedDeal?.id ||
      invoiceData?.data?.[0]?.deal_id ||
      (Number.isFinite(Number(id)) ? Number(id) : null);
    if (!dealId) {
      toast.error('Deal id not found');
      return;
    }
    if (verifiedCashCredit > 0) {
      toast.error(
        `Cannot write off while ₹${verifiedCashCredit.toLocaleString('en-IN')} verified cash credit remains. Apply, refund, or transfer it first.`
      );
      return;
    }
    if (!(saleRemainingBalance > 0)) {
      toast.error('No remaining balance to write off');
      return;
    }
    const remarks = String(adjustmentRemarks || '').trim();
    if (!remarks) {
      toast.error('Please add a short reason / remark');
      return;
    }
    setIsSubmitting(true);
    try {
      const issueDate = new Date().toISOString().slice(0, 10);
      let invoiceNumber = `WO/${dealId}/${Date.now()}`;
      if (adjustmentReason === ADJUSTMENT_REASON_PRICE_CORRECTION) {
        const target = pickPriceCorrectionTargetInvoice(allInvoices);
        if (!target?.id) {
          toast.error('No open invoice found to apply the price correction to');
          return;
        }
        invoiceNumber = buildPriceCorrectionInvoiceNumber(dealId, target.id);
      }
      const writtenOff = await claimExtraCharges({
        deal_id: dealId,
        source: INVOICE_SOURCE_CREDIT_NOTE,
        amount: saleRemainingBalance,
        gst_percentage: 0,
        invoiceNumber,
        issueDate,
        type: 'Builder',
        reason_code: adjustmentReason,
        remarks
      });
      if (!writtenOff?.id) {
        toast.error('Credit note was not created');
        return;
      }
      toast.success(
        adjustmentReason === ADJUSTMENT_REASON_PRICE_CORRECTION
          ? `Price-correction credit note of ₹${new Intl.NumberFormat('en-IN').format(
              saleRemainingBalance
            )} created.`
          : `Remaining balance of ₹${new Intl.NumberFormat('en-IN').format(
              saleRemainingBalance
            )} written off via Credit Note.`
      );
      setShowRemainingWriteOffModal(false);
      await refreshDealMoney();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not write off remaining balance'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmApplyCredit = async () => {
    const dealId =
      closedDeal?.id ||
      invoiceData?.data?.[0]?.deal_id ||
      (Number.isFinite(Number(id)) ? Number(id) : null);
    if (!dealId) {
      toast.error('Deal id not found');
      return;
    }
    if (!creditAvailable) {
      toast.error('No verified cash credit available');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await applyDealCreditBalance(dealId, {
        remarks: 'Applied from verified cash credit'
      });
      toast.success(
        `Applied ₹${new Intl.NumberFormat('en-IN').format(
          res?.credit_applied || res?.data?.credit_applied || 0
        )} from verified cash credit`
      );
      await refreshDealMoney();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Could not apply credit'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmVerifyLegacyCredit = async () => {
    const dealId =
      closedDeal?.id ||
      invoiceData?.data?.[0]?.deal_id ||
      (Number.isFinite(Number(id)) ? Number(id) : null);
    const amount = Number(reconcile.legacy_unverified_credit || 0);
    if (!dealId || !(amount > 0)) {
      toast.error('No unverified legacy credit to verify');
      return;
    }
    setIsSubmitting(true);
    try {
      await verifyLegacyCredit(dealId, {
        amount,
        remarks: 'Finance verified legacy credit_balance into ledger'
      });
      toast.success(
        `Verified ₹${amount.toLocaleString('en-IN')} into verified cash credit`
      );
      await refreshDealMoney();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Verify legacy credit failed'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadAllocationReport = async () => {
    const dealId =
      closedDeal?.id ||
      invoiceData?.data?.[0]?.deal_id ||
      (Number.isFinite(Number(id)) ? Number(id) : null);
    if (!dealId) return;
    setAllocationLoading(true);
    try {
      const res = await getAllocationReport(dealId);
      setAllocationReport(res?.data || res);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Allocation report failed'
      );
    } finally {
      setAllocationLoading(false);
    }
  };

  // Load the receipt report the first time the reconciliation panel opens.
  useEffect(() => {
    if (reconcilePanelOpen && !allocationReport && !allocationLoading) {
      loadAllocationReport();
    }
    // Only reconcilePanelOpen: loadAllocationReport is rebuilt every render, so
    // listing it would re-fire the fetch the guards above exist to prevent.
  }, [reconcilePanelOpen]);

  const [tallyFormOpen, setTallyFormOpen] = useState(false);

  const allocationTotals = useMemo(() => {
    const rows = allocationReport?.rows || [];
    const sum = (key) => rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
    return {
      count: rows.length,
      received: sum('amount'),
      applied: sum('allocated'),
      parked: sum('parked_credit'),
      gap: Number(allocationReport?.total_allocation_gap) || sum('allocation_gap')
    };
  }, [allocationReport]);

  // Tally ledger vs Housingram's net amount to collect (both = what the buyer still owes).
  const tallyCheck = useMemo(() => {
    const tally = reconcile?.tally_ledger_balance;
    if (tally == null || !Number.isFinite(Number(tally))) return { state: 'missing' };
    const diff = Math.round(Number(tally) - (Number(remainingDue) || 0));
    return { state: diff === 0 ? 'match' : 'differs', tally: Number(tally), diff };
  }, [reconcile?.tally_ledger_balance, remainingDue]);

  const submitTallyImportForDeal = async () => {
    const dealId =
      closedDeal?.id ||
      invoiceData?.data?.[0]?.deal_id ||
      (Number.isFinite(Number(id)) ? Number(id) : null);
    const bal = Number(tallyBalanceInput);
    if (!dealId || !Number.isFinite(bal)) {
      toast.error('Enter a valid Tally ledger balance');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await importTallyLedger({
        source_file: `manual-invoice-details-${dealId}`,
        rows: [
          {
            deal_id: dealId,
            ledger_balance: Math.round(bal),
            as_of_date: tallyAsOf || null
          }
        ]
      });
      const data = res?.data || res;
      toast.success(
        `Tally import: ${data?.imported ?? 0} imported, ${data?.skipped ?? 0} skipped`
      );
      setTallyBalanceInput('');
      setTallyFormOpen(false);
      await refreshDealMoney();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Tally import failed'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryInvoicePriorities = async () => {
    const dealId =
      closedDeal?.id ||
      invoiceData?.data?.[0]?.deal_id ||
      (Number.isFinite(Number(id)) ? Number(id) : null);
    if (!dealId) return;
    setIsSubmitting(true);
    try {
      const res = await retryInvoicePriorities(dealId);
      toast.success(
        `Priorities ensured (created ${res?.data?.created ?? res?.created ?? 0})`
      );
      await Promise.all([refreshDealMoney(), refetchStageUnlock()]);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Retry priorities failed'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showKycPanel && dealIdForKyc && (
        <AccountsKycPanel
          dealId={dealIdForKyc}
          dealLabel={
            invoiceData?.data?.[0]?.closedDeal?.unit?.unitName
              ? `${invoiceData.data[0].closedDeal.unit.unitName} · ${
                  invoiceData.data[0]?.closedDeal?.lead?.user?.name || 'Customer'
                }`
              : `Deal #${dealIdForKyc}`
          }
          onClose={() => setShowKycPanel(false)}
        />
      )}
      <Layout>
        <Loader isVisible={isLoading} />

        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Invoice Details' }} backArrow={true} onBack={onBack} />
        </div>

        <div
          className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''} mt-[5rem]`}>
          <GridWrapper width={100}>
            {/* Original two-card layout — equal width, even spacing */}
            {invoiceData?.data && invoiceData.data.length > 0 && (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 flex flex-col">
                  <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    Customer Information
                  </h3>
                  <div className="space-y-3 flex-1">
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-gray-600">Name:</span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {invoiceData.data[0]?.closedDeal?.lead?.user?.name || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-gray-600">Phone:</span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {invoiceData.data[0]?.closedDeal?.lead?.user?.phone_number || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-gray-600">Email:</span>
                      <span className="text-sm font-medium text-gray-800 text-right break-all">
                        {invoiceData.data[0]?.closedDeal?.lead?.user?.email || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4 items-center">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span
                        className={`text-sm font-medium px-2 py-1 rounded-full ${
                          invoiceData.data[0]?.closedDeal?.lead?.user?.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        {invoiceData.data[0]?.closedDeal?.lead?.user?.is_active
                          ? 'Active'
                          : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 flex flex-col">
                  <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    Close Deal Information
                    <span
                      className={`ml-auto text-sm font-medium px-3 py-1 rounded-full ${
                        invoiceData.data[0]?.closedDeal?.status === 'Approved'
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : invoiceData.data[0]?.closedDeal?.status === 'Cancelled' ||
                              invoiceData.data[0]?.closedDeal?.status === 'Rejected'
                            ? 'bg-red-100 text-red-800 border border-red-300'
                            : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                      }`}>
                      {invoiceData.data[0]?.closedDeal?.status || 'N/A'}
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 flex-1">
                    <div className="flex justify-between gap-2">
                      <span className="text-sm text-gray-600">Unit Name:</span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {invoiceData.data[0]?.closedDeal?.unit?.unitName || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Total Price:
                        <InfoTip help={DEAL_HELP.totalPrice} size={12} />
                      </span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {formatInrAmount(
                          dueBreakdown.closedSale ?? dueBreakdown.totalPrice
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-600">KYC:</span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${kycVerifyBadgeClasses(
                            kycVerification.status
                          )}`}>
                          {kycVerification.label}
                        </span>
                        {dealIdForKyc && (
                          <button
                            type="button"
                            onClick={() => setShowKycPanel(true)}
                            className="text-xs font-medium text-[#346C75] hover:underline">
                            View
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Cash allocated to invoices:
                        <InfoTip help={DEAL_HELP.cashAllocated} size={12} />
                      </span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {formatInrAmount(
                          reconcile.cash_allocated_to_invoices ?? dueBreakdown.paid
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-sm text-gray-600">Block:</span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {invoiceData.data[0]?.closedDeal?.unit?.block?.name || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-sm text-gray-600">Accommodation:</span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {invoiceData.data[0]?.closedDeal?.unit?.accommodation?.name ||
                          'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Open invoice balance:
                        <InfoTip help={DEAL_HELP.openInvoiceBalance} size={12} />
                      </span>
                      <span className="text-sm font-semibold text-gray-900 text-right">
                        {formatInrAmount(openInvoiceBalance)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-sm text-gray-600">Project:</span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {invoiceData.data[0]?.closedDeal?.unit?.project?.name || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Verified cash credit:
                        <InfoTip help={DEAL_HELP.verifiedCredit} size={12} />
                      </span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {formatInrAmount(
                          reconcile.verified_cash_credit ??
                            reconcile.cash_credit_balance ??
                            0
                        )}
                      </span>
                    </div>
                    {(reconcile.legacy_unverified_credit || 0) > 0 && (
                      <div className="flex justify-between gap-2">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Legacy credit (unverified):
                        <InfoTip help={DEAL_HELP.legacyCredit} size={12} />
                      </span>
                        <span className="text-sm font-medium text-amber-700 text-right">
                          {formatInrAmount(reconcile.legacy_unverified_credit)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Net amount to collect:
                        <InfoTip help={DEAL_HELP.netToCollect} size={12} />
                      </span>
                      <span className="text-sm font-semibold text-gray-900 text-right">
                        {formatInrAmount(netAmountToCollect)}
                      </span>
                    </div>
                    {(reconcile.commercial_credit_applied || 0) > 0 && (
                      <div className="flex justify-between gap-2">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Commercial credit applied:
                        <InfoTip help={DEAL_HELP.commercialCredit} size={12} />
                      </span>
                        <span className="text-sm font-medium text-gray-800 text-right">
                          {formatInrAmount(reconcile.commercial_credit_applied)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between gap-2">
                      <span className="text-sm text-gray-600">Deal Closed:</span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {invoiceData.data[0]?.closedDeal?.closed_at
                          ? new Date(
                              invoiceData.data[0]?.closedDeal?.closed_at
                            ).toLocaleDateString('en-IN')
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Tally ledger:
                        <InfoTip help={DEAL_HELP.tallyLedger} size={12} />
                      </span>
                      <span className="text-sm font-medium text-gray-800 text-right">
                        {reconcile.tally_ledger_balance == null
                          ? 'Not imported'
                          : formatInrAmount(reconcile.tally_ledger_balance)}
                      </span>
                    </div>
                    {lateFeeDue > 0 && (
                      <div className="flex justify-between gap-2 col-span-2">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                        Late fees due:
                        <InfoTip help={DEAL_HELP.lateFeesDue} size={12} />
                      </span>
                        <span className="text-sm font-medium text-gray-800 text-right">
                          {formatInrAmount(lateFeeDue)}
                        </span>
                      </div>
                    )}
                  </div>
                  {(pricingMismatch ||
                    reconcile.status === 'unallocated_credit' ||
                    reconcile.status === 'allocation_gap' ||
                    (netAmountToCollect === 0 && lateFeeDue === 0)) && (
                    <p
                      className={`mt-4 text-xs leading-snug px-3 py-2 rounded-md ${
                        pricingMismatch
                          ? 'bg-amber-50 text-amber-900 border border-amber-100'
                          : netAmountToCollect === 0
                            ? 'bg-emerald-50 text-emerald-900 border border-emerald-100'
                            : 'bg-sky-50 text-sky-900 border border-sky-100'
                      }`}>
                      {pricingMismatch
                        ? `Schedule ${formatInrAmount(reconcile.schedule_total)} is above closed sale ${formatInrAmount(reconcile.closed_sale)} by ${formatInrAmount(reconcile.pricing_overage)}. Use Price correction — not a new payment.`
                        : netAmountToCollect === 0
                          ? `Settled. Closed sale ${formatInrAmount(reconcile.closed_sale)}${
                              reconcile.net_schedule != null &&
                              reconcile.net_schedule !== reconcile.schedule_total
                                ? ` · net schedule ${formatInrAmount(reconcile.net_schedule)}`
                                : ''
                            }${
                              (reconcile.cash_credit_balance || 0) > 0
                                ? ` · cash credit ${formatInrAmount(reconcile.cash_credit_balance)} held`
                                : ''
                            }.`
                          : reconcile.status_label}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { key: 'Invoice', label: 'Invoice' },
                  { key: 'Payments', label: 'Payments' },
                  { key: 'Cheque', label: 'Cheques' },
                  {
                    key: 'Cancelled',
                    label: `Cancelled${
                      cancelledInvoices.length > 0
                        ? ` (${cancelledInvoices.length})`
                        : ''
                    }`
                  }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`h-9 px-4 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {invoiceData?.data && invoiceData.data.length > 0 && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {invoiceData?.data[0]?.source !== 'Extra Charge' && (
                    <Button
                      onClick={() => setShowClaimExtraChargesModal(true)}
                      displayType="secondary">
                      Claim Extra Charges
                    </Button>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Button
                      onClick={() => setShowDebitCreditNoteModal(true)}
                      displayType="secondary">
                      Add Debit / Credit Note
                    </Button>
                    <InfoTip help={DEAL_HELP.creditDebitNotes} />
                  </span>
                  {activeTab === 'Cheque' && (
                    <Button onClick={() => setShowChequeModal(true)}>Add Cheque</Button>
                  )}
                  {activeTab === 'Invoice' && (
                    <>
                      <Button
                        displayType="secondary-red"
                        disabled={
                          isSubmitting || lateFeeWriteOffCandidates.length === 0
                        }
                        title={
                          lateFeeWriteOffCandidates.length === 0
                            ? 'No unpaid late-fee invoices on this deal'
                            : 'Request cancel of unpaid late-fee invoices'
                        }
                        onClick={() => setShowLateFeeWriteOffModal(true)}>
                        Write off late fees
                        {lateFeeWriteOffCandidates.length > 0
                          ? ` (${lateFeeWriteOffCandidates.length})`
                          : ''}
                      </Button>
                      {creditAvailable && remainingDue > 0 && (
                        <Button
                          displayType="secondary"
                          disabled={isSubmitting}
                          onClick={confirmApplyCredit}>
                          Apply verified credit
                        </Button>
                      )}
                      {(reconcile.legacy_unverified_credit || 0) > 0 && (
                        <Button
                          displayType="secondary"
                          disabled={isSubmitting}
                          title="Move legacy credit_balance into verified cash credit ledger"
                          onClick={confirmVerifyLegacyCredit}>
                          Verify legacy credit
                        </Button>
                      )}
                      <Button
                        displayType="secondary-red"
                        disabled={
                          isSubmitting ||
                          !(saleRemainingBalance > 0) ||
                          verifiedCashCredit > 0
                        }
                        title={
                          verifiedCashCredit > 0
                            ? 'Apply/refund verified cash credit before write-off'
                            : saleRemainingBalance > 0
                              ? pricingMismatch
                                ? 'Create price-correction credit note (does not change closed sale)'
                                : 'Write off remaining sale balance via credit note'
                              : 'Nothing left to write off / correct'
                        }
                        onClick={openAdjustmentModal}>
                        {pricingMismatch
                          ? 'Price correction'
                          : 'Write off remaining balance'}
                      </Button>
                      <Button
                        disabled={pricingMismatch && remainingDue > 0}
                        title={
                          pricingMismatch
                            ? 'Cash already matches closed sale — use Price correction, not Pay'
                            : 'Pay deal (one invoice can take multiple installments)'
                        }
                        onClick={() => {
                          if (pricingMismatch && remainingDue > 0) {
                            toast.error(
                              'Open balance is a pricing mismatch. Use Price correction — do not record another payment.'
                            );
                            return;
                          }
                          setStatusId(invoiceData?.data[0]?.deal_id);
                          setDueAmount(remainingDue);
                          setIsPaid(true);
                          setCommonPay(true);
                        }}>
                        Pay
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {activeTab === 'Invoice' && stageUnlockRows.length > 0 && (
              <div className="w-full mb-3 rounded-lg border border-gray-200 bg-white overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setStageUnlockOpen((v) => !v)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-bold text-gray-900">
                        Stage unlock
                      </div>
                      <div className="mt-1 text-sm font-medium text-gray-700 truncate">
                        <span className="font-semibold text-gray-900">
                          {stageUnlockRows.filter((s) => s.state === 'paid').length}
                        </span>{' '}
                        paid
                        <span className="mx-1.5 text-gray-400">·</span>
                        <span className="font-semibold text-gray-900">
                          {
                            stageUnlockRows.filter((s) => s.state === 'invoiced')
                              .length
                          }
                        </span>{' '}
                        invoiced
                        <span className="mx-1.5 text-gray-400">·</span>
                        <span className="font-semibold text-gray-900">
                          {
                            stageUnlockRows.filter((s) => s.state === 'unlocked')
                              .length
                          }
                        </span>{' '}
                        unlocked
                        <span className="mx-1.5 text-gray-400">·</span>
                        <span className="font-semibold text-gray-900">
                          {
                            stageUnlockRows.filter(
                              (s) => s.state === 'not_unlocked'
                            ).length
                          }
                        </span>{' '}
                        locked
                      </div>
                    </div>
                  </button>
                  <Button
                    displayType="secondary"
                    disabled={isSubmitting}
                    title="Ensure AccountPriorities exist for completed stages"
                    onClick={handleRetryInvoicePriorities}>
                    Retry generate invoices
                  </Button>
                  <button
                    type="button"
                    aria-label={stageUnlockOpen ? 'Collapse' : 'Expand'}
                    onClick={() => setStageUnlockOpen((v) => !v)}
                    className="shrink-0 rounded p-1 text-gray-700 hover:bg-gray-100">
                    <svg
                      className={`h-5 w-5 transition-transform ${
                        stageUnlockOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.25}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
                {stageUnlockOpen && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                      {stageUnlockRows.map((s) => {
                        const state = String(s.state || 'not_unlocked');
                        const tone =
                          state === 'paid'
                            ? 'border-green-200 bg-green-50 text-green-900'
                            : state === 'invoiced'
                              ? 'border-blue-200 bg-blue-50 text-blue-900'
                              : state === 'unlocked'
                                ? 'border-amber-200 bg-amber-50 text-amber-950'
                                : 'border-gray-200 bg-gray-50 text-gray-700';
                        return (
                          <div
                            key={s.stage}
                            title={
                              state === 'not_unlocked'
                                ? 'Stage not unlocked by management'
                                : state
                            }
                            className={`rounded-md border px-2.5 py-2 ${tone}`}>
                            <div className="truncate text-sm font-semibold text-gray-900">
                              {s.stage}
                            </div>
                            <div className="mt-0.5 text-xs font-medium capitalize leading-tight">
                              {state.replace(/_/g, ' ')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'Invoice' && stageUnlockRows.length === 0 && (
              <div className="mb-3 flex justify-end">
                <Button
                  displayType="secondary"
                  disabled={isSubmitting}
                  title="Ensure AccountPriorities exist for completed stages"
                  onClick={handleRetryInvoicePriorities}>
                  Retry generate invoices
                </Button>
              </div>
            )}

            {activeTab === 'Invoice' && (
              <div className="w-full mb-4 rounded-lg border border-gray-200 bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setReconcilePanelOpen((v) => !v)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-base font-bold text-gray-900">
                      Accounts reconciliation
                      <InfoTip help={RECONCILE_HELP.panel} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                      {allocationReport ? (
                        allocationTotals.gap > 0 ? (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            {formatInrAmount(allocationTotals.gap)} of receipts unexplained
                          </span>
                        ) : (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                            All {allocationTotals.count} receipts accounted for
                          </span>
                        )
                      ) : (
                        <span>Check every payment received is applied, and match against Tally</span>
                      )}
                      {tallyCheck.state === 'match' && (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                          Tally matches
                        </span>
                      )}
                      {tallyCheck.state === 'differs' && (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                          Tally differs by {formatInrAmount(Math.abs(tallyCheck.diff))}
                        </span>
                      )}
                    </div>
                  </div>
                  <svg
                    className={`h-5 w-5 shrink-0 text-gray-700 transition-transform ${
                      reconcilePanelOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {reconcilePanelOpen && (
                  <div className="border-t border-gray-100 px-4 py-4 space-y-6">
                    {/* 1. Payments received */}
                    <section>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Payments received</h3>
                          <p className="text-xs text-gray-500">
                            Every payment should be applied to invoices or held as credit. Anything left is unexplained.
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={allocationLoading}
                          onClick={loadAllocationReport}
                          className="text-xs font-medium text-[#346C75] hover:underline disabled:opacity-50">
                          {allocationLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                      </div>

                      {allocationLoading && !allocationReport ? (
                        <p className="text-sm text-gray-500">Loading payments…</p>
                      ) : !allocationReport ? (
                        <p className="text-sm text-gray-500">Could not load payments. Try Refresh.</p>
                      ) : allocationTotals.count === 0 ? (
                        <p className="text-sm text-gray-500">No payments recorded for this deal yet.</p>
                      ) : (
                        <>
                          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                            {[
                              {
                                label: 'Received',
                                value: allocationTotals.received,
                                help: RECONCILE_HELP.received
                              },
                              {
                                label: 'Applied to invoices',
                                value: allocationTotals.applied,
                                help: RECONCILE_HELP.applied
                              },
                              {
                                label: 'Held as credit',
                                value: allocationTotals.parked,
                                help: RECONCILE_HELP.held
                              },
                              {
                                label: 'Unexplained',
                                value: allocationTotals.gap,
                                warn: allocationTotals.gap > 0,
                                help: RECONCILE_HELP.unexplained
                              }
                            ].map((tile) => (
                              <div
                                key={tile.label}
                                className={`rounded-md border px-3 py-2 ${
                                  tile.warn ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
                                }`}>
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  {tile.label}
                                  <InfoTip help={tile.help} size={12} />
                                </div>
                                <div
                                  className={`mt-0.5 text-base font-semibold tabular-nums ${
                                    tile.warn ? 'text-amber-800' : 'text-gray-900'
                                  }`}>
                                  {formatInrAmount(tile.value)}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="max-h-64 overflow-auto rounded-md border border-gray-200">
                            <table className="w-full min-w-[720px] text-left text-sm">
                              <thead className="sticky top-0 bg-gray-50 text-xs text-gray-600">
                                <tr>
                                  <th className="px-3 py-2 font-medium">Payment</th>
                                  <th className="px-3 py-2 font-medium text-right">Amount</th>
                                  <th className="px-3 py-2 font-medium text-right">Applied to invoices</th>
                                  <th className="px-3 py-2 font-medium text-right">Held as credit</th>
                                  <th className="px-3 py-2 font-medium text-right">Unexplained</th>
                                  <th className="px-3 py-2 font-medium">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(allocationReport.rows || []).map((r) => (
                                  <tr key={r.history_id} className="border-t border-gray-100 text-gray-700">
                                    <td className="px-3 py-2 align-top">
                                      <div className="max-w-[240px] truncate font-medium text-gray-800" title={r.transactionID || ''}>
                                        {r.transactionID || `Receipt ${r.history_id}`}
                                      </div>
                                      <div className="mt-0.5 text-xs text-gray-500">
                                        {[r.mode, `Receipt ${r.history_id}`].filter(Boolean).join(' · ')}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums align-top">{formatInrAmount(r.amount)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums align-top">{formatInrAmount(r.allocated)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums align-top">{formatInrAmount(r.parked_credit)}</td>
                                    <td
                                      className={`px-3 py-2 text-right tabular-nums align-top ${
                                        r.allocation_gap > 0 ? 'font-semibold text-amber-800' : ''
                                      }`}>
                                      {formatInrAmount(r.allocation_gap)}
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                      {r.allocation_gap > 0 ? (
                                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                                          Needs review
                                        </span>
                                      ) : (
                                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                          Accounted for
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </section>

                    {/* 2. Tally */}
                    <section className="border-t border-gray-100 pt-5">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Match with Tally</h3>
                          <p className="text-xs text-gray-500">
                            Enter the buyer&apos;s ledger balance from Tally to check it against Housingram.
                          </p>
                        </div>
                        {!tallyFormOpen && (
                          <Button displayType="secondary" onClick={() => setTallyFormOpen(true)}>
                            {tallyCheck.state === 'missing' ? 'Enter Tally balance' : 'Update Tally balance'}
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            Housingram: net amount to collect
                            <InfoTip help={RECONCILE_HELP.housingramNet} size={12} />
                          </div>
                          <div className="mt-0.5 text-base font-semibold tabular-nums text-gray-900">
                            {formatInrAmount(remainingDue || 0)}
                          </div>
                        </div>
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            Tally ledger balance
                            <InfoTip help={RECONCILE_HELP.tallyBalance} size={12} />
                          </div>
                          <div className="mt-0.5 text-base font-semibold tabular-nums text-gray-900">
                            {tallyCheck.state === 'missing' ? '—' : formatInrAmount(tallyCheck.tally)}
                          </div>
                        </div>
                        <div
                          className={`rounded-md border px-3 py-2 ${
                            tallyCheck.state === 'match'
                              ? 'border-emerald-200 bg-emerald-50'
                              : tallyCheck.state === 'differs'
                                ? 'border-red-200 bg-red-50'
                                : 'border-gray-200 bg-gray-50'
                          }`}>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            Result
                            <InfoTip help={RECONCILE_HELP.result} size={12} />
                          </div>
                          <div
                            className={`mt-0.5 text-base font-semibold ${
                              tallyCheck.state === 'match'
                                ? 'text-emerald-700'
                                : tallyCheck.state === 'differs'
                                  ? 'text-red-700'
                                  : 'text-gray-500'
                            }`}>
                            {tallyCheck.state === 'match'
                              ? 'Matches'
                              : tallyCheck.state === 'differs'
                                ? `Differs by ${formatInrAmount(Math.abs(tallyCheck.diff))}`
                                : 'Not entered yet'}
                          </div>
                        </div>
                      </div>

                      {tallyFormOpen && (
                        <div className="mt-3 grid grid-cols-1 gap-3 rounded-md border border-gray-200 p-3 sm:grid-cols-[minmax(0,1fr)_10.5rem_auto] sm:items-end">
                          <div>
                            <label className="mb-1 block text-xs text-gray-500">Tally ledger balance (₹)</label>
                            <input
                              type="number"
                              className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-800 focus:border-[#346C75] focus:outline-none focus:ring-1 focus:ring-[#346C75]"
                              value={tallyBalanceInput}
                              onChange={(e) => setTallyBalanceInput(e.target.value)}
                              placeholder="e.g. 100000"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs text-gray-500">As of date</label>
                            <input
                              type="date"
                              className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-800 focus:border-[#346C75] focus:outline-none focus:ring-1 focus:ring-[#346C75]"
                              value={tallyAsOf}
                              onChange={(e) => setTallyAsOf(e.target.value)}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button displayType="secondary" onClick={() => setTallyFormOpen(false)}>
                              Cancel
                            </Button>
                            <Button disabled={isSubmitting} onClick={submitTallyImportForDeal}>
                              Save
                            </Button>
                          </div>
                        </div>
                      )}
                    </section>
                  </div>
                )}
              </div>
            )}

            {(activeTab === 'Invoice' || activeTab === 'Cancelled') && (
              <div className="relative">
                <div className="overflow-x-auto hide-scrollbar w-full relative rounded-t-lg">
                  <table className="text-left w-full min-w-[1200px]">
                    <thead
                      className="bg-gray-200 !text-gray-600 w-full"
                      style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                      <tr className="font-normal h-[3rem] w-full whitespace-nowrap">
                        <th className="px-4 py-2 border-b font-normal cursor-pointer">
                          Invoice Number
                        </th>
                        <th className="px-4 py-2 border-b font-normal">Source</th>
                        <th className="px-4 py-2 border-b font-normal cursor-pointer">
                          Invoice Amount
                        </th>
                        <th className="px-4 py-2 border-b font-normal">Paid Amount</th>
                        <th className="px-4 py-2 border-b font-normal relative">
                          <div className="flex items-center justify-start gap-3">
                            <span>Due Amount</span>
                            <div className="ml-1 flex flex-col items-center justify-center">
                              <button
                                className={`w-4 h-4 ${
                                  sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'
                                }`}
                                onClick={() => handleSortByDueAmount('asc')}>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M5 12l5-5 5 5H5z" />
                                </svg>
                              </button>
                              <button
                                className={`w-4 h-4 -mt-1 ${
                                  sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'
                                }`}
                                onClick={() => handleSortByDueAmount('desc')}>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M15 8l-5 5-5-5h10z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </th>
                        <th className="px-4 py-2 border-b font-normal">Late Fee</th>
                        <th className="px-4 py-2 border-b font-normal">Status</th>
                        <th className="px-4 py-2 border-b font-normal">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoicesForTab.length > 0 ? (
                        invoicesForTab.map((poItem, index) => {
                          const paidAmount = poItem?.paid_amount || 0;
                          const isCancelled =
                            poItem?.cancelled || poItem?.status === 'Cancelled';
                          const isCancelPending = !!poItem?.cancel_requested && !isCancelled;
                          const isCreditNote = isCreditNoteInvoice(poItem);
                          const isPriceCorrection = isPriceCorrectionCreditNote(poItem);
                          const pcApplied = isCancelled
                            ? 0
                            : priceCorrectionAppliedToInvoice(poItem?.id, allInvoices);
                          const dueAmount = isCancelled
                            ? 0
                            : isCreditNote
                              ? 0
                              : invoiceOutstandingContribution(poItem, allInvoices);
                          const settledByPriceCorrection =
                            !isCancelled &&
                            !isCreditNote &&
                            pcApplied > 0 &&
                            dueAmount === 0;
                          const appliedToId = isPriceCorrection
                            ? getPriceCorrectionTargetInvoiceId(poItem, allInvoices)
                            : null;
                          // latefee invoices store the fee in amount; stages use late_fees col
                          const lateFee = isCancelled
                            ? 0
                            : invoiceLateFeeColumnAmount(poItem);

                          return (
                            <React.Fragment key={poItem?.id || index}>
                              <tr className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                                <td className="px-4 py-2 text-sm">
                                  <div>
                                    {poItem?.invoiceNumber ||
                                      (poItem?.id != null ? `#${poItem.id}` : '---')}
                                  </div>
                                  {isPriceCorrection && appliedToId != null && (
                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                      Applied to #{appliedToId}
                                    </div>
                                  )}
                                  {settledByPriceCorrection && pcApplied > 0 && (
                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                      CN applied {formatInrAmount(pcApplied)}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm">{poItem?.source || '---'}</td>{' '}
                                <td className="px-4 py-2 text-sm">
                                  {isCreditNote ? '−' : ''}₹
                                  {new Intl.NumberFormat('en-IN').format(poItem?.amount)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  ₹{new Intl.NumberFormat('en-IN').format(paidAmount)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  ₹{new Intl.NumberFormat('en-IN').format(dueAmount)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  ₹{new Intl.NumberFormat('en-IN').format(lateFee)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <div className="relative z-0">
                                    <span className="flex pl-6 overflow-hidden text-sm">
                                      {isCancelled
                                        ? 'Cancelled'
                                        : isCancelPending
                                          ? 'Cancel Pending'
                                          : isPriceCorrection
                                            ? 'Applied'
                                            : settledByPriceCorrection
                                              ? 'Settled by price correction'
                                              : poItem?.status || '---'}
                                    </span>
                                    <div
                                      className={`w-[5px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[4px] top-[-2px] ${
                                        isCancelled
                                          ? 'bg-gray-400'
                                          : isCancelPending
                                            ? 'bg-[#E97B37]'
                                          : isPriceCorrection || settledByPriceCorrection
                                            ? 'bg-[#3F8D78]'
                                          : ['unpaid', 'Unpaid'].includes(poItem?.status)
                                            ? 'bg-[#C2281B]' // Red for unpaid/unapproved
                                            : ['paid', 'Paid'].includes(poItem?.status)
                                              ? 'bg-[#3F8D78]' // Green for paid/approved
                                              : [
                                                    'pending',
                                                    'Pending',
                                                    'Ongoing',
                                                    'invite-sent',
                                                    'Not Completed',
                                                    'Material Dispatch',
                                                    'Available',
                                                    'Medium',
                                                    'Confirm Delivery'
                                                  ].includes(poItem?.status)
                                                ? 'bg-[#E97B37]' // Orange for pending/medium
                                                : 'bg-gray-400' // Default gray
                                      }`}>
                                      &nbsp;
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center justifycenter w-full relative gap-2">
                                    {/* Download button - only show if status is not "paid" */}

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleInvoicePaymentStagesDownload({
                                          id: poItem.deal_id,
                                          sourceName: poItem.source
                                        });
                                      }}
                                      className="h-9 w-9 grid place-items-center bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200"
                                      title="Download">
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                      </svg>
                                    </button>

                                    {/* Request cancel for any active invoice; pay only when unpaid */}
                                    {activeTab === 'Invoice' &&
                                      poItem?.status !== 'Cancelled' &&
                                      !poItem.cancelled &&
                                      !isCancelPending && (
                                        <>
                                          {!isCreditNote &&
                                            !settledByPriceCorrection &&
                                            dueAmount > 0 &&
                                            poItem?.status !== 'paid' &&
                                            poItem?.status !== 'Paid' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isStageUnlockedForInvoice(poItem)) {
                                                  toast.error(
                                                    'Stage not unlocked by management'
                                                  );
                                                  return;
                                                }
                                                const totalAmount = Number(dueAmount);
                                                handleStatus(poItem.id, totalAmount);
                                              }}
                                              disabled={!isStageUnlockedForInvoice(poItem)}
                                              className={`h-9 px-3 inline-flex items-center justify-center rounded-md text-sm gap-1 transition-colors duration-200 ${
                                                isStageUnlockedForInvoice(poItem)
                                                  ? 'bg-green-600 hover:bg-green-700 text-white'
                                                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                              }`}
                                              title={
                                                isStageUnlockedForInvoice(poItem)
                                                  ? `Pay (remaining due ₹${Number(
                                                      dueAmount
                                                    ).toLocaleString('en-IN')})`
                                                  : 'Stage not unlocked by management'
                                              }>
                                              <span className="text-base">₹</span>
                                              <span>Pay</span>
                                            </button>
                                          )}

                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleWaveOff(poItem.id);
                                            }}
                                            className="h-9 w-9 inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200"
                                            title="Request cancel">
                                            <XCircle size={16} />
                                          </button>
                                        </>
                                      )}

                                    {/* <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCancelInvoiceModal(poItem);
                                      }}
                                      className="h-9 px-3 inline-flex items-center justify-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 rounded-md transition-colors duration-200 border border-red-200"
                                      title="Cancel Invoice">
                                      <img
                                        src={cancelDealIcon}
                                        alt="cancel_deal"
                                        className="w-4 h-4"
                                      />
                                      Cancel
                                    </button> */}
                                  </div>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center py-10 align-middle">
                            <div className="flex flex-col items-center justify-center w-full h-full">
                              <h2 className="text-lg font-bold text-gray-800 mb-1">
                                No Data Found
                              </h2>
                              <p className="text-gray-600 text-sm">
                                {activeTab === 'Cancelled'
                                  ? 'No cancelled invoices.'
                                  : 'No invoice data available.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'Payments' && <SalesPayment chequeData={chequeData} dealID={id} />}

            {activeTab === 'Cheque' && (
              <ChequeTable
                chequeData={chequeData}
                onAddCheque={() => setShowChequeModal(true)}
                onViewCheque={handleViewCheque}
                onDeleteCheque={handleDeleteCheque}
              />
            )}
          </GridWrapper>
        </div>
      </Layout>

      {showCancelInvoiceModal && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Request Cancel Approval'}
          onClose={() => setShowCancelInvoiceModal(false)}
          width={30}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Request management approval to cancel this invoice?
            </p>
            <p className="text-center text-sm text-gray-600 mt-2">
              The invoice stays active until management approves.
            </p>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => setShowCancelInvoiceModal(false)}>
              No
            </Button>
            <Button type="button" disabled={isSubmitting} onClick={confirmCancelInvoice}>
              Request Cancel
            </Button>
          </div>
        </Modal>
      )}

      {/* Waive-off Confirmation Modal */}
      {showWaveOffModal && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Request Cancel Approval'}
          onClose={() => setShowWaveOffModal(false)}
          width={30}>
          <div className="flex flex-col justify-center align-center">
            <p className="text-center">
              Request management approval to cancel this invoice?
            </p>
            <p className="text-center text-sm text-gray-600 mt-2">
              The invoice stays active until management approves.
            </p>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => setShowWaveOffModal(false)}>
              No
            </Button>
            <Button type="button" disabled={isSubmitting} onClick={confirmWaveOff}>
              Request Cancel
            </Button>
          </div>
        </Modal>
      )}

      {showLateFeeWriteOffModal && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name="Write off late fees"
          onClose={() => !isSubmitting && setShowLateFeeWriteOffModal(false)}
          width={35}>
          <div className="flex flex-col gap-2">
            <p className="text-center text-sm text-gray-800">
              Request management approval to cancel{' '}
              <strong>{lateFeeWriteOffCandidates.length}</strong> unpaid late fee invoice
              {lateFeeWriteOffCandidates.length === 1 ? '' : 's'} on this deal?
            </p>
            <p className="text-center text-sm text-gray-600">
              Total outstanding late fees: ₹
              {new Intl.NumberFormat('en-IN').format(Math.round(lateFeeWriteOffTotal))}
            </p>
            <p className="text-center text-xs text-gray-500 mt-1">
              Uses the same cancel → management approval flow as a single invoice cancel. Sale
              stage invoices are not touched.
            </p>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              disabled={isSubmitting}
              onClick={() => setShowLateFeeWriteOffModal(false)}>
              No
            </Button>
            <Button type="button" disabled={isSubmitting} onClick={confirmLateFeeWriteOff}>
              {isSubmitting ? 'Requesting…' : 'Request write-off'}
            </Button>
          </div>
        </Modal>
      )}

      {showRemainingWriteOffModal && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={
            adjustmentReason === ADJUSTMENT_REASON_PRICE_CORRECTION
              ? 'Price correction'
              : 'Write off remaining balance'
          }
          onClose={() => !isSubmitting && setShowRemainingWriteOffModal(false)}
          width={40}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-gray-800 text-center">
              Create a Credit Note for{' '}
              <strong>
                ₹{new Intl.NumberFormat('en-IN').format(saleRemainingBalance)}
              </strong>
              . Original invoices and payments stay as-is.
            </p>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1">
              <div className="flex justify-between">
                <span>Closed sale</span>
                <span>{formatInrAmount(reconcile.closed_sale)}</span>
              </div>
              <div className="flex justify-between">
                <span>Cash allocated to invoices</span>
                <span>
                  {formatInrAmount(
                    reconcile.cash_allocated_to_invoices ?? dueBreakdown.paid
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Invoice schedule</span>
                <span>{formatInrAmount(reconcile.schedule_total)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span>Open invoice balance</span>
                <span>{formatInrAmount(saleRemainingBalance)}</span>
              </div>
            </div>
            <label className="text-sm text-gray-700">
              Adjustment type
              <select
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={adjustmentReason}
                onChange={(e) => {
                  const next = e.target.value;
                  setAdjustmentReason(next);
                  if (next === ADJUSTMENT_REASON_PRICE_CORRECTION) {
                    setAdjustmentRemarks(
                      defaultPriceCorrectionRemarks(
                        reconcile.closed_sale,
                        reconcile.schedule_total
                      )
                    );
                  }
                }}>
                <option value={ADJUSTMENT_REASON_PRICE_CORRECTION}>
                  Price correction (schedule above closed sale)
                </option>
                <option value={ADJUSTMENT_REASON_COMMERCIAL_WRITE_OFF}>
                  Commercial write-off (waive remaining receivable)
                </option>
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Reason / remark
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[72px]"
                value={adjustmentRemarks}
                onChange={(e) => setAdjustmentRemarks(e.target.value)}
                placeholder="Why this adjustment is needed"
              />
            </label>
            <p className="text-xs text-gray-500">
              {adjustmentReason === ADJUSTMENT_REASON_PRICE_CORRECTION
                ? 'Does not record new cash. Prefer this when Tally already shows ₹0 due.'
                : 'Use only when management intentionally waives a valid receivable.'}{' '}
              Late fees: use Write off late fees.
            </p>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              disabled={isSubmitting}
              onClick={() => setShowRemainingWriteOffModal(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={confirmRemainingBalanceWriteOff}>
              {isSubmitting
                ? 'Saving…'
                : adjustmentReason === ADJUSTMENT_REASON_PRICE_CORRECTION
                  ? 'Create price-correction CN'
                  : 'Write off remaining balance'}
            </Button>
          </div>
        </Modal>
      )}

      <PaymentForm
        isOpen={isPaid}
        onClose={resetPaidModal}
        invoiceData={invoiceData}
        dueAmount={dueAmount}
        commonPay={commonPay}
        statusId={StatusId}
        chequeData={chequeData}
        onSuccess={() => {
          refreshDealMoney();
        }}
      />

      {showChequeModal && (
        <AddChequeForm
          isOpen={showChequeModal}
          onClose={() => setShowChequeModal(false)}
          onSubmit={handleChequeSubmit}
          invoiceData={invoiceData}
          sanitizeToNumber={sanitizeToNumber}
          isSubmitting={isCreatingCheques}
        />
      )}

      {/* Claim Extra Charges Modal */}
      {showClaimExtraChargesModal && (
        <ClaimExtraChargesForm
          isOpen={showClaimExtraChargesModal}
          onClose={() => setShowClaimExtraChargesModal(false)}
          dealId={id}
          onSuccess={handleClaimExtraChargesSuccess}
          projectId={invoiceData.data[0]?.closedDeal?.unit?.project?.id}
        />
      )}

      {showDebitCreditNoteModal && (
        <DebitCreditNoteForm
          isOpen={showDebitCreditNoteModal}
          onClose={() => setShowDebitCreditNoteModal(false)}
          dealId={id}
          onSuccess={handleDebitCreditNoteSuccess}
        />
      )}
    </>
  );
};

export default InvoiceDetails;
